import http from "../../core/services/http.service";
import {INVOICE_TYPE_CREDIT_NOTE, SOURCE_INVOICE_PAYMENTS_TABLE, SOURCE_INVOICES_TABLE} from "@/modules/constants";
import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
import {hasPermission} from "@/core/directives/permission";
import i18n from "@/shared/i18n";
import EventBus from "@/plugins/event-bus";
import ExportsService from "@/core/services/exports.service";
import {AppSettings} from "@/shared/AppSettings";

const InvoicesService = {
  createInvoice(data, expand = 'invoice_lots,miscellaneous_items') {
    return http.post(`/api/v1/invoices/?expand=${expand}`, data);
  },
  updateInvoice(uuid, data, expand = 'invoice_lots,miscellaneous_items') {
    return http.put(`/api/v1/invoices/${uuid}/?expand=${expand}`, data);
  },
  generateSingleBatchInvoices(saleNo, data) {
    return http.post(`/api/v1/invoices/batch/${saleNo}/`, data);
  },
  batchCancelInvoices(data) {
    return http.patch(`/api/v1/invoices/batch/status/`, data);
  },
  approveInvoices(data) {
    return http.patch(`/api/v1/invoices/batch/approve-invoices/`, data);
  },
  getInvoices(params = null) {
    return http.get('/api/v1/invoices/', {params});
  },
  getInvoice(uuid, params = {}) {
    return http.get(`/api/v1/invoices/${uuid}/`, {params});
  },
  patchInvoice(uuid, data = {}) {
    return http.patch(`/api/v1/invoices/${uuid}/`, data);
  },
  getInvoicePdfData(invoiceUuid) {
    return http.get(`/api/v1/invoices/${invoiceUuid}/pdf-data/`);
  },
  generateMergedPdfForInvoices(data) {
    return http.post(`/api/v1/invoices/generate-merged-pdf/`, data);
  },
  createPayment(data) {
    return http.post(`/api/v1/invoices/payments/`, data);
  },
  updatePayment(uuid, data, expand = '') {
    return http.put(`/api/v1/invoices/payments/${uuid}/?expand=${expand}`, data);
  },
  removeInvoicePayment(uuid) {
    return http.delete(`/api/v1/invoices/payment/${uuid}/`)
  },
  sendOverdueEmails(data) {
    return http.post(`/api/v1/invoices/overdue/notify/`, data);
  },
  getPayment(id, params) {
    return http.get(`/api/v1/payments/${id}/`, {params});
  },
  sendUnpaidEmails(data) {
    return http.post(`/api/v1/invoices/unpaid/notify/`, data);
  },
  resendInvoices(data) {
    return http.post(`/api/v1/invoices/resend/notify/`, data);
  },
  getInvoicesTotalAmounts(params = null) {
    return http.get('/api/v1/dashboard/invoices/', {params});
  },
  calculateData(data, expand) {
    return http.post(`/api/v1/invoices/calculate/?expand=${expand}`, data);
  },
  markInvoicesAsDispatched(data) {
    return http.post(`/api/v1/invoices/mark-dispatched/`, data);
  },
  unmarkInvoicesAsDispatched(data) {
    return http.post(`/api/v1/invoices/unmark-dispatched/`, data);
  },
  markAllItemsCollected(uuids) {
    return http.post(`/api/v1/invoices/mark-collected/`, {
      uuids
    });
  },
  markInvoiceItemsCollected(data) {
    return http.put(`/api/v1/invoices/financial-items/mark-collected/`, data);
  },
  createCreditNote(data) {
    return http.post(`/api/v1/invoices/credit-note/`, data);
  },
  updateCreditNote(data) {
    return http.patch(`/api/v1/invoices/${data.invoice_uuid}/`, data);
  },
  sendCreditNoteEmails(data) {
    return http.post(`/api/v1/invoices/credit-note/notify/`, data);
  },
  partialUpdatePayment(uuid, data) {
    return http.patch(`/api/v1/payments/${uuid}/`, data);
  },
  bulkEditInvoices(data) {
    return http.patch(`/api/v1/invoices/bulk-edit/`, data);
  },
  approveAutoPayment(data) {
    return http.post(`/api/v1/invoices/approve-auto-payment/`, data);
  },
  allocatePayment(data) {
    return http.post(`/api/v1/payments/allocate-invoice-payment/`, data);
  },
  undoPaymentAllocation(data) {
    return http.post(`/api/v1/payments/undo-allocate-invoice-payment/`, data);
  },
  createBatchPayment(data) {
    return http.post(`/api/v1/payments/batch-payments/`, data);
  },
  updateBatchPayment(uuid, data) {
    return http.put(`/api/v1/payments/batch-payments/${uuid}/`, data);
  },
  getBatchPayment(uuid, params) {
    return http.get(`/api/v1/payments/batch-payments/${uuid}/`, {params});
  },
  getInvoicePaymentsTotalAmounts(params = null) {
    return http.get('/api/v1/dashboard/invoice-payments/', {params});
  },

  async viewInvoicePdf(vm, invoice, onlyView = false, itemsCollectedCallback = null) {
    const modalActions = [];
    if (!onlyView && hasPermission('change_invoice') &&
      !['deposit', 'service', INVOICE_TYPE_CREDIT_NOTE].includes(invoice.type) &&
      !['cancelled', 'credited'].includes(invoice.status)) {
      modalActions.push({
        display_name: i18n.t('Mark item(s) Collected'),
        handler: () => itemsCollectedCallback(invoice)
      });
    }
    EventBus.$emit(ON_PDF_MODAL_OPEN, {
      title: i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no}),
      actions: modalActions
    });
    let response = await InvoicesService.getInvoicePdfData(invoice.uuid);
    if (response.success) {
      EventBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
        ...response.body,
        filename: i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
      })
    } else {
      EventBus.$emit(ON_PDF_MODAL_CLOSE);
      let errorMessage = "";
      if (response.body.non_field_errors) {
        errorMessage = response.body.non_field_errors[0];
      } else {
        errorMessage = response.body;
      }
      console.log(this);
      vm.$errorDialog(errorMessage);
    }
  },
  async onEditPayment(vm, invoicePayment, source) {

    // check for batch payment validity
    if (invoicePayment.payment.batch_payment && invoicePayment.payment.batch_payment.is_approved) {
      let msg = vm.$i18n.t(
        'This payment is part of an approved batch payment that has not yet been exported. Editing it may affect the batch payment. If you proceed, the batch payment will be updated automatically to reflect these changes. Batch ID: {id}. Do you want to proceed?',
        {id: invoicePayment.payment.batch_payment.payment_no}
      )

      let success = await vm.$confirm(msg)
      if (!success) {
        return false
      }
    }

    vm.showPaymentModal({object: {}, source: source, invoices: [], type: invoicePayment.payment.type});
    let response = await InvoicesService.getPayment(invoicePayment.payment.uuid, {expand: 'invoice_payments.invoice.currency'});
    if (response.success) {
      vm.updatePaymentModalObject({object: response.body});
    }
  },
  async onDeletePayment(vm, invoicePayment, source) {
    let msg = vm.$i18n.t("Are you sure you want to delete following payment?")

    if (invoicePayment.payment.batch_payment && invoicePayment.payment.batch_payment.is_approved) {
      msg = vm.$i18n.t(
        'This payment is part of an approved batch payment that has not yet been exported. Deleting it may affect the batch payment. If you proceed, the batch payment will be updated automatically to reflect these changes. Batch ID: {id}. Do you want to proceed?',
        {id: invoicePayment.payment.batch_payment.payment_no}
      )
    }

    let success = await vm.$confirm(
      msg,
      vm.$i18n.t('Payment was successfully deleted'),
      InvoicesService.removeInvoicePayment,
      invoicePayment.uuid,
    );

    if (success) {
      if (source === SOURCE_INVOICES_TABLE) {
        await vm.getTableData();
        await vm.updatePaymentListModalInvoice();
        if (vm.invoice.invoice_payments.length === 0) {
          vm.hidePaymentListModal();
        }
      } else if (source === SOURCE_INVOICE_PAYMENTS_TABLE) {
        await vm.getTableData();
      }
    }
  },
  async onOverpaymentUndo(vm, invoicePayment, source)  {
    let confirm = await vm.$confirm(vm.$i18n.t('Reversing overpayment allocation will create reverse payments for both invoices. Do you wish to proceed and undo overpayment allocation?'))
    if (!confirm) {
      return false
    }
    const response = await InvoicesService.undoPaymentAllocation({payment_uuid: invoicePayment.payment.uuid})
    if (response.success) {
      if (source === SOURCE_INVOICES_TABLE) {
        await vm.getTableData();
        vm.updatePaymentListModalInvoice();
      } else if (source === SOURCE_INVOICE_PAYMENTS_TABLE) {
        await vm.getTableData();
      }
    } else {
      if (response.body.non_field_errors) {
        vm.$errorDialog(response.body.non_field_errors[0]);
      }
    }
  },
  updateInvoiceExtraInfo(data) {
    return http.patch(`/api/v1/invoices/edit-invoice-extra-info/`, data);
  },
  async downloadShippingImportTemplate() {
    let response = await ExportsService.generateToken();
    if (!response.success) {
      return false;
    }
    window.open(AppSettings.url() + `/api/v1/invoices/shipping-import-template/?token=${response.body.token}`, '_blank')
  },
  uploadShippingImport(data, onUploadProgress) {
    return http.post('/api/v1/invoices/shipping-import/', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onUploadProgress
    });
  },
  sendCollectionAppointmentEmails(data) {
    return http.post(`/api/v1/invoices/collection-appointment/notify/`, data);
  },
};

export default InvoicesService;
